import { createContext, useContext } from 'react'
import type { CodeError, SignInError } from './auth'

/**
 * Quem está usando o app e em qual clínica (docs/api/me.md, `GET /me`). Papel e clínica decidem o
 * que a interface mostra; a decisão que vale é sempre a da API (RN-AUT-20).
 */

export type Role = 'root' | 'manager' | 'receptionist' | 'practitioner'

const ROLE_LABELS: Record<Role, string> = {
  root: 'Westhetics',
  manager: 'Gestora',
  receptionist: 'Recepção',
  practitioner: 'Prestadora',
}

/**
 * O papel por extenso, como a pessoa lê (RT-01; os mesmos rótulos de `private.role_label()` no
 * banco). O valor cru do enum nunca vai para a tela; root é a Westhetics.
 */
export function roleLabel(role: string): string {
  return ROLE_LABELS[role as Role] ?? role
}

/** Vínculo em que a pessoa pode entrar: ativo ou convidado, em clínica não bloqueada (RN-AUT-12). */
export function isUsableClinic(clinic: { status: string }): boolean {
  return clinic.status === 'active' || clinic.status === 'invited'
}

export interface ClinicSummary {
  id: string
  name: string
  role: string
  isDefault: boolean
}

export interface Profile {
  userId: string
  name: string
  email: string
  isRoot: boolean
  role: Role
  clinicId: string | null
  activeClinic: ClinicSummary | null
  clinics: (ClinicSummary & { status: string; practitionerId: string | null })[]
  mustChangePassword: boolean
  hasPassword: boolean
  hasTemporaryPassword: boolean
  lastSignInAt: string | null
}

export interface Session {
  /**
   * `password_required`: a pessoa entrou com a senha temporária, pela senha, e só pode definir a
   * nova (RT-04, RN-AUT-05). Não há perfil ainda: a API recusa `GET /me` até a troca. Entrar com
   * código nunca chega a este estado (T48).
   */
  status: 'loading' | 'signed_out' | 'password_required' | 'signed_in'
  profile: Profile | null
  /** Por que a entrada não se completou, quando a conta entrou mas a API recusou o perfil. */
  problem: string | null
  /** Root sem clínica ativa: a visão da Westhetics (painel de clínicas, catálogo, contratos). */
  isGlobalRoot: boolean
  /** Gestora, ou root agindo numa clínica: pode administrar a clínica ativa (RT-02, RT-03). */
  canManageClinic: boolean
  /**
   * Logo depois de entrar, quem tem mais de uma clínica utilizável (ou é root) escolhe onde entrar
   * (RN-AUT-11, P-AUT-02): a porta de entrada leva a `/choose-clinic`. Vale até a escolha; não
   * volta a ser perguntado na mesma sessão, nem ao recarregar a página (P-AUT-18).
   */
  mustChooseClinic: boolean
  /**
   * Instante (ms) em que a pessoa entrou por código nesta sessão, lido do token; `null` se entrou
   * com senha, ou depois de definir a senha. Decide se a aba Senha de Sua conta dispensa a senha
   * atual nos 10 minutos depois do código (RN-AUT-07). Nada é oferecido por causa dele (T48).
   */
  codeSignInAt: number | null
  signIn(email: string, password: string): Promise<{ error: SignInError | null }>
  /** Entra com o código de 6 dígitos recebido por e-mail (`verifyOtp`, RN-AUT-07). */
  verifyCode(email: string, code: string): Promise<{ error: CodeError | null }>
  signOut(): Promise<void>
  /**
   * Troca a clínica ativa (`PUT /me/clinic`) e renova o token. `null` volta à visão global do root.
   * `isDefault` marca a clínica como a padrão da pessoa (RN-AUT-10).
   */
  switchClinic(clinicId: string | null, options?: { isDefault?: boolean }): Promise<void>
  /** Renova o token e relê a sessão. */
  refresh(): Promise<void>
  /**
   * A senha acabou de ser definida (`PUT /me/password`): renova o token, que vem sem a marca de
   * troca obrigatória, relê o perfil e fecha a janela do código desta entrada (a senha atual volta
   * a ser pedida). Se o token não renovar, a sessão é encerrada: a pessoa entra de novo, já com a
   * senha nova.
   */
  passwordChanged(): Promise<void>
}

export const SessionContext = createContext<Session | null>(null)

export const PROFILE_QUERY_KEY = ['me'] as const

/**
 * Primeiro item da chave das leituras públicas, feitas antes de haver sessão (o convite, em
 * `GET /access/invite`). Não dependem de quem entrou: sobrevivem à abertura da sessão.
 */
export const PUBLIC_QUERY_SCOPE = 'access'

export function useSession(): Session {
  const session = useContext(SessionContext)
  if (!session) throw new Error('useSession precisa do AppProviders.')
  return session
}
